import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Sync as SyncIcon,
  CheckCircle,
  Sync as SyncIcon2,
  Refresh as RefreshIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import RevolutIcon from '../components/RevolutIcon';
import { useSearchParams, useLocation } from 'react-router-dom';
import { api } from '../contexts/ApiContext';

// Import the individual page components
import Transactions from './Transactions';
import RevolutExpenses from './RevolutExpenses';
import RevolutCounterparties from './RevolutCounterparties';
import Webhooks from './Webhooks';
import RevolutLinking from './RevolutLinking';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`revolut-tabpanel-${index}`}
      aria-labelledby={`revolut-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `revolut-tab-${index}`,
    'aria-controls': `revolut-tabpanel-${index}`,
  };
}

const Revolut: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [syncCompletedAt, setSyncCompletedAt] = useState<Date | null>(null);
  const prevSyncInProgressRef = useRef(false);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<{
    status: string;
    message: string;
    error_code?: string;
    details?: string;
    status_code?: number;
    account?: string;
    timestamp?: string;
    uk_connection?: {
      status: string;
      message: string;
      account?: string;
      env_gaps?: string[];
      details?: string;
      error_code?: string;
      status_code?: number;
      timestamp?: string;
    };
  } | null>(null);
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  
  // Tab names mapping
  const tabNames = ['transactions', 'expenses', 'counterparties', 'webhooks', 'linking'];
  const tabNameToIndex: { [key: string]: number } = {
    'transactions': 0,
    'expenses': 1,
    'counterparties': 2,
    'webhooks': 3,
    'linking': 4
  };
  
  // Determine initial tab from URL param or route path
  let initialTab = 0;
  let tabToSet = 'transactions'; // Default tab
  const tabParam = searchParams.get('tab');
  if (tabParam) {
    // Check if it's a named tab
    if (tabNameToIndex.hasOwnProperty(tabParam.toLowerCase())) {
      initialTab = tabNameToIndex[tabParam.toLowerCase()];
      tabToSet = tabParam.toLowerCase();
    } else {
      // Fallback: try to parse as number for backwards compatibility
      const tabNum = parseInt(tabParam, 10);
      if (!isNaN(tabNum) && tabNum >= 0 && tabNum < tabNames.length) {
        initialTab = tabNum;
        tabToSet = tabNames[tabNum];
        // Update URL to use named tab
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('tab', tabNames[tabNum]);
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  } else {
    // Set tab based on route path for backwards compatibility
    if (location.pathname.includes('revolut-expenses')) {
      initialTab = 1;
      tabToSet = 'expenses';
    } else if (location.pathname.includes('revolut-counterparties')) {
      initialTab = 2;
      tabToSet = 'counterparties';
    } else if (location.pathname.includes('webhooks')) {
      initialTab = 3;
      tabToSet = 'webhooks';
    } else if (location.pathname.includes('transactions')) {
      initialTab = 0;
      tabToSet = 'transactions';
    }
    // Default to transactions tab if no tab parameter is specified
    // Set the URL parameter
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabToSet);
    setSearchParams(newSearchParams, { replace: true });
  }
  
  const [tabValue, setTabValue] = useState(initialTab);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Update URL with named tab instead of number
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabNames[newValue]);
    setSearchParams(newSearchParams, { replace: true });
  };

  // Sync tab value with URL param or route path
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      // Check if it's a named tab
      if (tabNameToIndex.hasOwnProperty(tabParam.toLowerCase())) {
        setTabValue(tabNameToIndex[tabParam.toLowerCase()]);
      } else {
        // Fallback: try to parse as number for backwards compatibility
        const tabNum = parseInt(tabParam, 10);
        if (!isNaN(tabNum) && tabNum >= 0 && tabNum < tabNames.length) {
          setTabValue(tabNum);
          // Update URL to use named tab
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('tab', tabNames[tabNum]);
          setSearchParams(newSearchParams, { replace: true });
        }
      }
    } else if (location.pathname.includes('transactions')) {
      setTabValue(0);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', 'transactions');
      setSearchParams(newSearchParams, { replace: true });
    } else if (location.pathname.includes('revolut-expenses')) {
      setTabValue(1);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', 'expenses');
      setSearchParams(newSearchParams, { replace: true });
    } else if (location.pathname.includes('revolut-counterparties')) {
      setTabValue(2);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', 'counterparties');
      setSearchParams(newSearchParams, { replace: true });
    } else if (location.pathname.includes('webhooks')) {
      setTabValue(3);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', 'webhooks');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, location.pathname, setSearchParams]);

  const fetchSyncStatus = useCallback(async () => {
    const maxAttempts = 3;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await api.get('/api/revolut/sync-status');
        setSyncStatus(response.data.sync_status);
        return;
      } catch (error: any) {
        console.error('Error fetching sync status:', error);
        const isServerError = error.response?.status >= 500;
        if (attempt < maxAttempts - 1 && isServerError) {
          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
          continue;
        }
        break;
      }
    }
  }, []);

  const checkConnectionStatus = useCallback(async (options?: {
    autoOpenModalOnProblem?: boolean;
    autoCloseModalOnSuccess?: boolean;
  }) => {
    const autoOpenModalOnProblem = options?.autoOpenModalOnProblem ?? true;
    const autoCloseModalOnSuccess = options?.autoCloseModalOnSuccess ?? false;
    try {
      setCheckingConnection(true);
      const response = await api.get('/api/revolut/connection-status', {
        // Server runs UK probe + Ireland Revolut call in one request; 15s often trips on slow API / cold start
        timeout: 90000
      });
      const newStatus = {
        status: response.data.status,
        message: response.data.message,
        error_code: response.data.error_code,
        details: response.data.details,
        status_code: response.data.status_code,
        account: response.data.account,
        timestamp: response.data.timestamp,
        uk_connection: response.data.uk_connection,
      };
      setConnectionStatus(newStatus);

      const uk = response.data.uk_connection;
      const ukBad = uk && uk.status !== 'connected';

      // If Ireland status is error, or UK probe failed, optionally surface the modal
      // (page-load checks should not force-open and interrupt the user).
      if (response.data.status === 'error' || response.data.status === 'auth_failed') {
        if (autoOpenModalOnProblem) {
          setConnectionModalOpen(true);
        }
      } else if (response.data.status === 'connected' && !ukBad) {
        if (autoCloseModalOnSuccess) {
          setConnectionModalOpen(false);
        }
      } else if (response.data.status === 'connected' && ukBad) {
        if (autoOpenModalOnProblem) {
          setConnectionModalOpen(true);
        }
      }
    } catch (error: any) {
      console.error('Error checking connection status:', error);
      setConnectionStatus({
        status: 'error',
        message: error.response?.data?.message || 'Failed to check connection status',
        details: error.message,
        timestamp: new Date().toISOString()
      });
      if (autoOpenModalOnProblem) {
        setConnectionModalOpen(true);
      }
    } finally {
      setCheckingConnection(false);
    }
  }, []);

  // Fetch sync status and check connection on mount
  useEffect(() => {
    fetchSyncStatus();
    // On page load, auto-open details if connection/auth is failing.
    checkConnectionStatus({ autoOpenModalOnProblem: true });
  }, [fetchSyncStatus, checkConnectionStatus]);

  // Listen for custom events triggered from Transactions page to refresh status
  useEffect(() => {
    const handleSyncStatusRefresh = () => {
      fetchSyncStatus();
    };

    window.addEventListener('revolut-sync-status-refresh', handleSyncStatusRefresh);
    return () => {
      window.removeEventListener('revolut-sync-status-refresh', handleSyncStatusRefresh);
    };
  }, [fetchSyncStatus]);

  // Child Transactions page emits this while /sync-complete is actively running.
  useEffect(() => {
    const onSyncState = (event: Event) => {
      const custom = event as CustomEvent<{ running?: boolean }>;
      setSyncInProgress(Boolean(custom.detail?.running));
    };
    window.addEventListener('revolut-transactions-sync-state', onSyncState as EventListener);
    return () => {
      window.removeEventListener('revolut-transactions-sync-state', onSyncState as EventListener);
    };
  }, []);

  // Detect running -> not-running transition to show a short "just completed" recency.
  useEffect(() => {
    if (prevSyncInProgressRef.current && !syncInProgress) {
      setSyncCompletedAt(new Date());
      fetchSyncStatus();
    }
    prevSyncInProgressRef.current = syncInProgress;
  }, [syncInProgress, fetchSyncStatus]);

  // Automatic historical sync on page load is DISABLED
  // Users can manually trigger sync using the "Sync Revolut" or "Sync Historical Data" buttons
  // useEffect(() => {
  //   const syncAllData = async () => {
  //     setSyncInProgress(true);
  //     setSyncError(null);
  //     try {
  //       // Call the sync-all endpoint which handles historical vs incremental automatically
  //       // Use a very long timeout for historical syncs (can take 30-40 minutes)
  //       // TEMPORARILY: Only sync Ireland account - UK sync paused until IRE is stable
  //       const response = await api.post('/api/revolut/sync-all', {
  //         accounts: ['ireland'],  // UK sync paused - focusing on IRE first
  //         force_historical: false  // Let backend decide based on metadata
  //       }, {
  //         timeout: 2400000  // 40 minutes timeout for historical syncs
  //       });
  //       
  //       // Update sync status after sync completes
  //       const statusResponse = await api.get('/api/revolut/sync-status');
  //       setSyncStatus(statusResponse.data.sync_status);
  //       
  //       setSyncInProgress(false);
  //     } catch (error: any) {
  //       console.error('Error syncing Revolut data:', error);
  //       // Check if it's a timeout error
  //       if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
  //         setSyncError('Sync is taking longer than expected. This is normal for historical imports. Check back in a few minutes or check the sync status.');
  //       } else {
  //         setSyncError(error.response?.data?.error || 'Failed to sync Revolut data');
  //       }
  //       setSyncInProgress(false);
  //     }
  //   };
  //
  //   // Sync on initial load (async, non-blocking)
  //   syncAllData();
  // }, []);

  // Map tab index to data type
  const tabToDataType: { [key: number]: string } = {
    0: 'transactions',
    1: 'expenses',
    2: 'counterparties',
    3: 'webhooks'
  };
  
  const currentDataType = tabToDataType[tabValue];
  const currentSyncData = syncStatus?.[currentDataType];
  const formatSyncDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not synced yet';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const irelandSync = currentSyncData?.ireland || currentSyncData?.Ireland || null;
  const ukSync = currentSyncData?.uk || currentSyncData?.UK || null;

  const irelandConnectionSuccessful = connectionStatus?.status === 'connected';
  const ukConnectionSuccessful = connectionStatus?.uk_connection?.status === 'connected';
  const irelandLastSyncLabelRaw = formatSyncDate(
    irelandSync?.last_incremental_sync_at || irelandSync?.last_sync_datetime
  );
  const ukLastSyncLabelRaw = formatSyncDate(
    ukSync?.last_incremental_sync_at || ukSync?.last_sync_datetime
  );
  const recentCompletionOverride =
    !syncInProgress &&
    syncCompletedAt != null &&
    Date.now() - syncCompletedAt.getTime() < 2 * 60 * 1000;
  const irelandLastSyncLabel = recentCompletionOverride ? '1m ago' : irelandLastSyncLabelRaw;
  const ukLastSyncLabel = recentCompletionOverride ? '1m ago' : ukLastSyncLabelRaw;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
          <RevolutIcon sx={{ mr: 2 }} />
          Revolut
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Open connection details">
              <IconButton
                size="small"
                onClick={() => setConnectionModalOpen(true)}
                aria-label="Open Revolut API connection details"
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={`Ireland: ${irelandConnectionSuccessful ? 'Successful connection' : 'Failed connection'} | Last sync ${irelandLastSyncLabel}`}
            >
              <Chip
                icon={
                  syncInProgress ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : irelandConnectionSuccessful ? (
                    <CheckCircle />
                  ) : (
                    <SyncIcon2 />
                  )
                }
                label={
                  syncInProgress
                    ? `IE: ${irelandConnectionSuccessful ? 'Successful connection' : 'Failed connection'} | Sync in progress...`
                    : `IE: ${irelandConnectionSuccessful ? 'Successful connection' : 'Failed connection'} | Last sync ${irelandLastSyncLabel}`
                }
                size="small"
                color={irelandConnectionSuccessful ? 'success' : 'error'}
                onClick={() => setConnectionModalOpen(true)}
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>
            {checkingConnection && <CircularProgress size={12} />}
          </Box>
          <Tooltip
            title={`UK: ${ukConnectionSuccessful ? 'Successful connection' : 'Failed connection'} | Last sync ${ukLastSyncLabel}`}
          >
            <Chip
              icon={
                syncInProgress ? (
                  <CircularProgress size={14} color="inherit" />
                ) : ukConnectionSuccessful ? (
                  <CheckCircle />
                ) : (
                  <SyncIcon2 />
                )
              }
              label={
                syncInProgress
                  ? `UK: ${ukConnectionSuccessful ? 'Successful connection' : 'Failed connection'} | Sync in progress...`
                  : `UK: ${ukConnectionSuccessful ? 'Successful connection' : 'Failed connection'} | Last sync ${ukLastSyncLabel}`
              }
              size="small"
              color={ukConnectionSuccessful ? 'success' : 'error'}
              onClick={() => setConnectionModalOpen(true)}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
        </Box>
      </Box>

      {syncError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {syncError}
        </Alert>
      )}

      {connectionStatus?.uk_connection &&
        connectionStatus.uk_connection.status !== 'connected' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" component="span">
            Revolut UK (server):{' '}
          </Typography>
          <Typography variant="body2" component="span">
            {connectionStatus.uk_connection.message}
          </Typography>
          {connectionStatus.uk_connection.env_gaps &&
            connectionStatus.uk_connection.env_gaps.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }} component="div">
              Add these on the API host (Hetzner VPS / production env for rrltd-api):{' '}
              {connectionStatus.uk_connection.env_gaps.join('; ')}
            </Typography>
          )}
          {connectionStatus.uk_connection.details && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {connectionStatus.uk_connection.details}
            </Typography>
          )}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="revolut tabs"
            variant="fullWidth"
          >
            <Tab label="Transactions" {...a11yProps(0)} />
            <Tab label="Expenses" {...a11yProps(1)} />
            <Tab label="Counterparties" {...a11yProps(2)} />
            <Tab label="Webhooks" {...a11yProps(3)} />
            <Tab label="Linking" {...a11yProps(4)} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {tabValue === 0 && <Transactions />}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {tabValue === 1 && <RevolutExpenses />}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {tabValue === 2 && <RevolutCounterparties />}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {tabValue === 3 && <Webhooks />}
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          {tabValue === 4 && <RevolutLinking />}
        </TabPanel>
      </Card>

      {/* Connection Details Modal */}
      <Dialog
        open={connectionModalOpen}
        onClose={() => setConnectionModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Revolut API Connection Details
        </DialogTitle>
        <DialogContent dividers>
          {connectionStatus ? (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={connectionStatus.status === 'connected' ? 'Connected' : 
                         connectionStatus.status === 'auth_failed' ? 'Authentication Failed' : 
                         'Error'}
                  color={
                    connectionStatus.status === 'connected' ? 'success' :
                    connectionStatus.status === 'auth_failed' ? 'error' :
                    'warning'
                  }
                  size="small"
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Message
                </Typography>
                <Typography variant="body1">
                  {connectionStatus.message}
                </Typography>
              </Box>

              {connectionStatus.error_code && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Error Code
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {connectionStatus.error_code}
                  </Typography>
                </Box>
              )}

              {connectionStatus.status_code && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    HTTP Status Code
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {connectionStatus.status_code}
                  </Typography>
                </Box>
              )}

              {connectionStatus.details && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {connectionStatus.details}
                  </Typography>
                </Box>
              )}

              {connectionStatus.account && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Account
                  </Typography>
                  <Typography variant="body2">
                    {connectionStatus.account}
                  </Typography>
                </Box>
              )}

              {connectionStatus.timestamp && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last Checked
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(connectionStatus.timestamp).toLocaleString()}
                  </Typography>
                </Box>
              )}

              {connectionStatus.uk_connection && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    UK account (separate OAuth app)
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={
                        connectionStatus.uk_connection.status === 'connected' ? 'Connected' :
                        connectionStatus.uk_connection.status === 'auth_failed' ? 'Authentication failed' :
                        connectionStatus.uk_connection.status === 'not_configured' ? 'Not configured' :
                        'Error'
                      }
                      color={
                        connectionStatus.uk_connection.status === 'connected' ? 'success' :
                        connectionStatus.uk_connection.status === 'auth_failed' ? 'error' :
                        'warning'
                      }
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {connectionStatus.uk_connection.message}
                  </Typography>
                  {connectionStatus.uk_connection.env_gaps &&
                    connectionStatus.uk_connection.env_gaps.length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Env gaps: {connectionStatus.uk_connection.env_gaps.join('; ')}
                    </Typography>
                  )}
                  {connectionStatus.uk_connection.details && (
                    <Typography variant="body2" color="text.secondary">
                      {connectionStatus.uk_connection.details}
                    </Typography>
                  )}
                </>
              )}

              {(connectionStatus.status === 'error' || connectionStatus.status === 'auth_failed') && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Troubleshooting Steps:
                  </Typography>
                  <Typography variant="body2" component="div">
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>Verify that REVOLUT_ACCESS_TOKEN is set in environment variables</li>
                      <li>If access token is expired, ensure REVOLUT_REFRESH_TOKEN and REVOLUT_JWT_ASSERTION are configured</li>
                      <li>Check API container logs on the Hetzner VPS for detailed error messages</li>
                      <li>If error code is 9001, the refresh token is incorrect - regenerate tokens</li>
                      <li>If status code is 500, Revolut API may be experiencing issues - try again later</li>
                    </ul>
                  </Typography>
                </Alert>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading connection status...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectionModalOpen(false)}>
            Close
          </Button>
          <Button
            onClick={async () => {
              await checkConnectionStatus({
                autoOpenModalOnProblem: true,
                autoCloseModalOnSuccess: false,
              });
              // Keep modal open after retry so the user can confirm success/failure in-place.
            }}
            variant="contained"
            startIcon={<RefreshIcon />}
            disabled={checkingConnection}
          >
            {checkingConnection ? 'Testing...' : 'Retry Connection'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Revolut;

